
import { useState } from 'react';
import { 
  Sidebar, 
  SidebarContent, 
  SidebarHeader, 
  SidebarMenu, 
  SidebarMenuButton, 
  SidebarMenuItem, 
  SidebarProvider,
  SidebarInset,
  SidebarTrigger
} from "@/components/ui/sidebar";
import { Building, Calendar as CalendarIcon, Ticket, DollarSign, Users, BarChart2, ShoppingBag, Megaphone, Wallet, LogOut } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, Activity } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";

// Import components for tabs
import VenueRoster from '@/components/admin/venue-oversight/VenueRoster';
import GigScheduling from '@/components/admin/venue-oversight/GigScheduling';
import TicketManagement from '@/components/admin/venue-oversight/TicketManagement';
import FinanceCenter from '@/components/admin/venue-oversight/FinanceCenter';
import TeamAccess from '@/components/admin/venue-oversight/TeamAccess';
import VenueInsights from '@/components/admin/venue-oversight/VenueInsights';
import Ticketing from '@/components/venue/Ticketing';
import VenueEcommerce from '@/components/venue/VenueEcommerce';
import VenuePromotions from '@/components/venue/VenuePromotions';
import VenueAnalytics from '@/components/venue/VenueAnalytics';
import CreatorWallet from '@/components/creator/CreatorWallet';
import VerificationReview from '@/components/admin/VerificationReview';
import PortalIdBadge from '@/components/admin/PortalIdBadge';
import RepresentationRequests from '@/components/admin/RepresentationRequests';

const VenueOversightDashboard = () => {
  const [activeTab, setActiveTab] = useState("venues");
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Logged out",
      description: "You have been successfully logged out.",
    });
    navigate("/landing-page-2");
  };

  const menuItems = [
    { id: "venues", label: "Venues", icon: Building, component: <VenueRoster /> },
    { id: "schedule", label: "Schedule", icon: CalendarIcon, component: <GigScheduling /> },
    { id: "tickets", label: "Tickets", icon: Ticket, component: <TicketManagement /> },
    { id: "finance", label: "Finance", icon: DollarSign, component: <FinanceCenter /> },
    { id: "verifications", label: "Verifications & Documents", icon: Building, component: <VerificationReview filterType="venue" /> },
    { id: "requests", label: "Representation Requests", icon: Users, component: <RepresentationRequests /> },
    { id: "team", label: "Team", icon: Users, component: <TeamAccess /> },
    { id: "insights", label: "Insights", icon: BarChart2, component: <VenueInsights /> },
    { id: "ticketing", label: "Venue Ticketing", icon: Ticket, component: <Ticketing /> },
    { id: "ecommerce", label: "Venue E-Commerce", icon: ShoppingBag, component: <VenueEcommerce /> },
    { id: "promotions", label: "Venue Promotions", icon: Megaphone, component: <VenuePromotions /> },
    { id: "analytics", label: "Venue Analytics", icon: BarChart2, component: <VenueAnalytics /> },
    { id: "wallet", label: "Wallet Management", icon: Wallet, component: <CreatorWallet /> },
  ];

  const activeMenuItem = menuItems.find(item => item.id === activeTab);

  return (
    <div className="min-h-screen bg-black text-white">
      <SidebarProvider>
        <div className="flex w-full min-h-screen">
          <Sidebar className="border-r border-white/20 bg-white text-black">
            <SidebarHeader className="p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <img 
                  src="/lovable-uploads/metro-city-groove-logo-white.png" 
                  alt="MCG Agency Logo" 
                  className="w-12 h-12 object-contain bg-white rounded-lg p-1 invert text-black"
                />
                <div>
                  <h1 className="text-lg font-bold text-black">Venue Oversight</h1>
                  <p className="text-xs text-gray-600">Management Dashboard</p>
                </div>
              </div>
            </SidebarHeader>
            
            <SidebarContent className="p-4">
              <SidebarMenu>
                {menuItems.map((item) => (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      onClick={() => setActiveTab(item.id)}
                      isActive={activeTab === item.id}
                      className={`w-full justify-start text-left px-4 py-3 rounded-lg transition-colors ${
                        activeTab === item.id 
                          ? 'bg-gray-200 text-black' 
                          : 'text-black hover:bg-gray-100 hover:text-black'
                      }`}
                    >
                      <item.icon className="w-5 h-5 mr-3 text-black" />
                      <span className="text-sm text-black">{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarContent>
          </Sidebar>

          <SidebarInset className="flex-1 bg-black">
            <header className="sticky top-0 flex h-16 shrink-0 items-center gap-2 border-b border-white/20 bg-black/95 backdrop-blur-md px-6">
              <SidebarTrigger className="text-white hover:bg-white/10" />
              <div className="flex items-center gap-2">
                {activeMenuItem && (
                  <>
                    <activeMenuItem.icon className="w-5 h-5 text-white" />
                    <h2 className="text-xl font-semibold text-white">{activeMenuItem.label}</h2>
                  </>
                )}
              </div>
              <div className="ml-auto flex items-center gap-3">
                <PortalIdBadge portalCode="VO" />
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </Button>
              </div>
            </header>

            <main className="flex-1 p-6">
              {/* Stats Cards */}
              <section className="mb-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card className="bg-white/5 border-white/20 text-white">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Today's Bookings</CardTitle>
                    <CalendarIcon className="h-4 w-4 text-gray-400" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">12</div>
                    <p className="text-xs text-gray-400">3 new since yesterday</p>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/20 text-white">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Top Earning Venue</CardTitle>
                    <DollarSign className="h-4 w-4 text-gray-400" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">Neon Garden</div>
                    <p className="text-xs text-gray-400">$78,000 this month</p>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/20 text-white">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Alerts</CardTitle>
                    <AlertTriangle className="h-4 w-4 text-yellow-400" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">3</div>
                    <p className="text-xs text-gray-400">2 booking conflicts</p>
                  </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/20 text-white">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Recent Activity</CardTitle>
                    <Activity className="h-4 w-4 text-gray-400" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">+5 new events</div>
                    <p className="text-xs text-gray-400">In the last 24 hours</p>
                  </CardContent>
                </Card>
              </section>

              {/* Active Component */}
              {activeMenuItem?.component}
            </main>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </div>
  );
};

export default VenueOversightDashboard;
